import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2, UserPlus, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import FileUpload from "@/components/FileUpload";

function FieldLabel({ children, required }) {
  return (
    <Label className="text-xs text-zinc-400">
      {children}
      {required && <span className="text-red-400 ml-1">*</span>}
    </Label>
  );
}

/**
 * Asset + Service tagging fields for the ticket flow. Both dropdowns are
 * scoped to the selected CDARK (`project_id`) — matches the same filter
 * behaviour used by the /issues EntityPage.
 */
function TicketTaggingFields({ form, update, opts, kind }) {
  const cdark = form.project_id;
  const filtered = (key) =>
    !cdark ? [] : opts(key).filter((o) => o.project_id === cdark);
  const assets = filtered("assets");
  const services = filtered("services");
  const placeholder = cdark ? "Select…" : "Select CDARK first";
  return (
    <>
      <div className="col-span-2 md:col-span-1">
        <FieldLabel>Asset Tagging (optional)</FieldLabel>
        <Select value={form.asset_id || ""} onValueChange={(v) => update("asset_id", v)}>
          <SelectTrigger className="mt-1.5 bg-zinc-900 border-white/10 text-white" data-testid={`raise-${kind}-asset`}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100 max-h-72">
            {assets.length === 0 ? (
              <div className="px-2 py-3 text-xs text-zinc-500">
                {cdark ? "No assets under this CDARK" : "Pick a CDARK first"}
              </div>
            ) : assets.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2 md:col-span-1">
        <FieldLabel>Service Tagging (optional)</FieldLabel>
        <Select value={form.service_id || ""} onValueChange={(v) => update("service_id", v)}>
          <SelectTrigger className="mt-1.5 bg-zinc-900 border-white/10 text-white" data-testid={`raise-${kind}-service`}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100 max-h-72">
            {services.length === 0 ? (
              <div className="px-2 py-3 text-xs text-zinc-500">
                {cdark ? "No services under this CDARK" : "Pick a CDARK first"}
              </div>
            ) : services.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}

/**
 * Inline editable list of ticket contacts. Each row captures {name, email,
 * phone}. Empty rows are stripped on submit by `submit()`.
 */
function ContactPersonsEditor({ contacts, onChange, kind }) {
  const update = (idx, patch) => {
    const next = contacts.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    onChange(next);
  };
  const remove = (idx) => onChange(contacts.filter((_, i) => i !== idx));
  const add = () => onChange([...contacts, { name: "", email: "", phone: "" }]);

  return (
    <div className="col-span-2 rounded-md border border-white/10 bg-zinc-950/40 p-3" data-testid={`raise-${kind}-contacts`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-zinc-400 font-medium">
          <UserPlus className="w-3.5 h-3.5" /> Contacts
          {contacts.length > 0 && <span className="normal-case tracking-normal text-zinc-500">({contacts.length})</span>}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={add}
          className="text-xs text-sky-400 hover:text-sky-300 hover:bg-white/5 h-7 px-2"
          data-testid={`raise-${kind}-contact-add`}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add contact
        </Button>
      </div>
      {contacts.length === 0 ? (
        <p className="text-[11px] text-zinc-500 italic px-1 py-2">
          Add one or more people to contact about this ticket. Optional.
        </p>
      ) : (
        <div className="space-y-2">
          {contacts.map((c, i) => (
            <div
              key={i}
              className="grid grid-cols-12 gap-2 items-start"
              data-testid={`raise-${kind}-contact-row-${i}`}
            >
              <Input
                value={c.name || ""}
                onChange={(e) => update(i, { name: e.target.value })}
                placeholder="Name"
                className="col-span-4 bg-zinc-900 border-white/10 text-white text-sm h-9"
                data-testid={`raise-${kind}-contact-name-${i}`}
              />
              <Input
                value={c.email || ""}
                onChange={(e) => update(i, { email: e.target.value })}
                placeholder="Email"
                type="email"
                className="col-span-4 bg-zinc-900 border-white/10 text-white text-sm h-9"
                data-testid={`raise-${kind}-contact-email-${i}`}
              />
              <Input
                value={c.phone || ""}
                onChange={(e) => update(i, { phone: e.target.value })}
                placeholder="Phone"
                className="col-span-3 bg-zinc-900 border-white/10 text-white text-sm h-9"
                data-testid={`raise-${kind}-contact-phone-${i}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(i)}
                className="col-span-1 h-9 px-0 text-zinc-500 hover:text-red-400 hover:bg-red-500/5"
                data-testid={`raise-${kind}-contact-remove-${i}`}
                title="Remove contact"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Generic right-side drawer to raise a Task or Ticket.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (v: boolean) => void
 *  - kind: "task" | "ticket"
 *  - referenceData: from useRefData()
 *  - myTeamIds: array of team IDs the current user belongs to
 *  - onCreated?: (doc) => void
 */
export default function RaiseEntityDrawer({ open, onOpenChange, kind, referenceData, myTeamIds = [], onCreated }) {
  const isTask = kind === "task";
  const title = isTask ? "Raise Task" : "Raise Ticket";
  const endpoint = isTask ? "/tasks" : "/issues";
  const subjectKey = "subject";

  // Two assignment modes per the spec:
  //   "own"   → assign to a member of one of my own teams (associate or self)
  //   "other" → route to a different team; leave assignee blank so that team's lead picks it up
  const [mode, setMode] = useState("own");

  const blankForm = isTask
    ? {
        subject: "",
        project_id: "",
        biz_project_id: "",
        team_id: "",
        assigned_user_id: "",
        priority: "Medium",
        status: "Open",
        due_date: "",
        details: "",
        files: [],
      }
    : {
        // Simplified ticket form (per product spec): no Project (biz),
        // no team routing, no manual status. Status is always "Open" —
        // enforced server-side by `POST /issues`.
        subject: "",
        description: "",
        project_id: "",
        asset_id: "",
        service_id: "",
        priority: "Medium",
        contact_persons: [],
        files: [],
      };

  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  // Compute teams partitioned by membership.
  const allTeams = referenceData?.teams || [];
  const myTeams = allTeams.filter((t) => myTeamIds.includes(t.value));
  const otherTeams = allTeams.filter((t) => !myTeamIds.includes(t.value));

  // Default mode + team selection when drawer opens.
  // Only relevant for tasks — tickets bypass the assign-to workflow entirely.
  useEffect(() => {
    if (!open) return;
    if (!isTask) {
      setForm(blankForm);
      return;
    }
    const initialMode = myTeams.length > 0 ? "own" : "other";
    setMode(initialMode);
    const initialTeam = initialMode === "own"
      ? (myTeams[0]?.value || "")
      : "";
    setForm({ ...blankForm, team_id: initialTeam });
  }, [open, kind, myTeamIds]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When mode flips, reset team_id + assignee so we don't leak stale values.
  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next);
    if (next === "own") {
      update("team_id", myTeams[0]?.value || "");
    } else {
      update("team_id", "");
    }
    update("assigned_user_id", "");
  };

  // Available assignees for "own" mode = lead + members of the selected team.
  const selectedTeam = allTeams.find((t) => t.value === form.team_id);
  const teamMemberIds = selectedTeam
    ? [selectedTeam.team_lead_id, ...(selectedTeam.members || [])].filter(Boolean)
    : [];
  const ownAssignees = (referenceData?.users || []).filter((u) => teamMemberIds.includes(u.value));

  const submit = async () => {
    if (!form.subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Drop empty contact rows (Name is the only required column client-side)
      // so we don't post `{name: "", email: "", phone: ""}` triples.
      if (Array.isArray(payload.contact_persons)) {
        payload.contact_persons = payload.contact_persons.filter(
          (c) => (c.name || "").trim() || (c.email || "").trim() || (c.phone || "").trim()
        );
      }
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      const { data } = await api.post(endpoint, payload);
      toast.success(`${isTask ? "Task" : "Ticket"} created — ${data.code}`);
      onCreated && onCreated(data);
      onOpenChange(false);
      referenceData?.refresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  // Build options from referenceData
  const opts = (k) => referenceData?.[k] || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-zinc-950 border-l border-white/10 text-zinc-100 sm:max-w-lg w-full overflow-y-auto"
        data-testid={`raise-${kind}-drawer`}
      >
        <SheetHeader>
          <SheetTitle className="text-white" style={{ fontFamily: "Outfit" }}>{title}</SheetTitle>
        </SheetHeader>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <FieldLabel required>Subject</FieldLabel>
            <Input
              value={form[subjectKey]}
              onChange={(e) => update(subjectKey, e.target.value)}
              className="mt-1.5 bg-zinc-900 border-white/10 text-white"
              placeholder={isTask ? "Short summary of the work" : "Short summary of the issue"}
              data-testid={`raise-${kind}-subject`}
              autoFocus
            />
          </div>

          <div className="col-span-2 md:col-span-1">
            <FieldLabel>CDARK (optional)</FieldLabel>
            <Select value={form.project_id || ""} onValueChange={(v) => {
              update("project_id", v);
              // Reset dependent selects when CDARK changes so we never
              // submit an asset/service that no longer belongs to the CDARK.
              if (!isTask) { update("asset_id", ""); update("service_id", ""); }
            }}>
              <SelectTrigger className="mt-1.5 bg-zinc-900 border-white/10 text-white" data-testid={`raise-${kind}-project`}>
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100 max-h-72">
                {opts("projects").map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isTask && (
            <div className="col-span-2 md:col-span-1">
              <FieldLabel>Project (optional)</FieldLabel>
              <Select value={form.biz_project_id || ""} onValueChange={(v) => update("biz_project_id", v)}>
                <SelectTrigger className="mt-1.5 bg-zinc-900 border-white/10 text-white" data-testid={`raise-${kind}-biz-project`}>
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100 max-h-72">
                  {opts("biz_projects_open").map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {!isTask && <TicketTaggingFields form={form} update={update} opts={opts} kind={kind} />}

          {/* "Assign To" removed from Task creation per product spec —
              tasks now land unassigned; leads triage from the queue. */}

          <div className="col-span-2 md:col-span-1">
            <FieldLabel>Priority</FieldLabel>
            <Select value={form.priority || "Medium"} onValueChange={(v) => update("priority", v)}>
              <SelectTrigger className="mt-1.5 bg-zinc-900 border-white/10 text-white" data-testid={`raise-${kind}-priority`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
                {opts("priorities").map((o) => {
                  const v = typeof o === "string" ? o : o.value;
                  const l = typeof o === "string" ? o : o.label;
                  return <SelectItem key={v} value={v}>{l}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>

          {isTask && (
          <div className="col-span-2 md:col-span-1">
            <FieldLabel>Status</FieldLabel>
            <Select value={form.status || "Open"} onValueChange={(v) => update("status", v)}>
              <SelectTrigger className="mt-1.5 bg-zinc-900 border-white/10 text-white" data-testid={`raise-${kind}-status`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
                {opts("statuses").map((o) => {
                  const v = typeof o === "string" ? o : o.value;
                  const l = typeof o === "string" ? o : o.label;
                  return <SelectItem key={v} value={v}>{l}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          )}

          {isTask && (
            <div className="col-span-2 md:col-span-1">
              <FieldLabel>Due Date</FieldLabel>
              <Input
                type="date"
                value={form.due_date || ""}
                onChange={(e) => update("due_date", e.target.value)}
                className="mt-1.5 bg-zinc-900 border-white/10 text-white"
                data-testid={`raise-${kind}-due`}
              />
            </div>
          )}

          <div className="col-span-2">
            <FieldLabel>{isTask ? "Task Details" : "Description"}</FieldLabel>
            <Textarea
              value={isTask ? form.details : form.description}
              onChange={(e) => update(isTask ? "details" : "description", e.target.value)}
              className="mt-1.5 bg-zinc-900 border-white/10 text-white min-h-[120px]"
              placeholder={isTask ? "What needs to be done?" : "What's the problem?"}
              data-testid={`raise-${kind}-details`}
            />
          </div>

          {/* Attachments — enabled for both tasks and tickets. */}
          <div className="col-span-2 rounded-md border border-white/10 bg-zinc-950/40 p-3" data-testid={`raise-${kind}-attachments`}>
            <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-zinc-400 font-medium">
              <Paperclip className="w-3.5 h-3.5" /> Attachments
              {(form.files || []).length > 0 && (
                <span className="normal-case tracking-normal text-zinc-500">({form.files.length})</span>
              )}
            </div>
            <FileUpload
              value={form.files || []}
              onChange={(next) => update("files", next)}
              testid={`raise-${kind}-files`}
            />
          </div>

          {!isTask && (
            <ContactPersonsEditor
              contacts={form.contact_persons || []}
              onChange={(next) => update("contact_persons", next)}
              kind={kind}
            />
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2 pb-4">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="text-zinc-300 hover:text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !form.subject.trim()}
            className="bg-white text-black hover:bg-zinc-200 font-medium"
            data-testid={`raise-${kind}-submit`}
          >
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Create {isTask ? "Task" : "Ticket"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
